import React, { useState, useEffect } from 'react';
import { Menu, X, Terminal } from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const navItems = [
    { id: 'hero', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'experience', label: 'Experience' },
    { id: 'projects', label: 'Projects' },
    { id: 'widgets', label: 'Dashboard' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section
      const sections = navItems.map(item => item.id);
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-50 md:hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(10, 6, 3, 0.96) 0%, rgba(15, 9, 5, 0.94) 50%, rgba(20, 12, 8, 0.92) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Header */}
            <div className="flex items-center justify-between px-6" style={{ height: '4.5rem', borderBottom: '1px solid rgba(255, 232, 200, 0.1)' }}>
              <button
                onClick={() => scrollToSection('hero')}
                className="flex items-center gap-1.5"
              >
                <Terminal 
                  className="w-7 h-7 transition-all duration-300" 
                  style={{ 
                    color: '#cab297',
                    filter: 'drop-shadow(0 0 8px rgba(202, 178, 151, 0.3))'
                  }} 
                />
              </button>
              
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '50%',
                  background: 'rgba(20, 12, 8, 0.8)',
                  backdropFilter: 'blur(10px)',
                  color: '#ffe8c8',
                  transition: 'all 0.3s ease',
                }}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Menu Items */}
            <div className="px-6 py-8 space-y-3 overflow-y-auto">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '1rem 1.5rem',
                    borderRadius: '1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    fontFamily: 'Fredoka, sans-serif',
                    transition: 'all 0.3s ease',
                    ...(activeSection === item.id ? {
                      background: 'linear-gradient(135deg, rgba(255, 232, 200, 0.3) 0%, rgba(237, 197, 143, 0.4) 100%)',
                      color: '#ffe8c8',
                      boxShadow: '0 0 20px rgba(255, 232, 200, 0.3)',
                    } : {
                      background: 'rgba(255, 232, 200, 0.05)',
                      color: '#edc58f',
                    })
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? 'nav-scrolled'
            : 'bg-transparent'
        } ${isMobileMenuOpen ? 'md:block hidden' : ''}`}
        style={{
          ...(isScrolled && {
            background: 'linear-gradient(180deg, rgba(10, 6, 3, 0.98) 0%, rgba(15, 9, 5, 0.95) 50%, rgba(20, 12, 8, 0.92) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
          })
        }}
      >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="flex items-center justify-between" style={{ height: '4.5rem' }}>
          {/* Logo */}
          <button
            onClick={() => scrollToSection('hero')}
            className="flex items-center gap-1.5"
            style={{
              transition: 'all 0.3s ease',
            }}
          >
            <Terminal 
              className="w-7 h-7 transition-all duration-300" 
              style={{ 
                color: '#cab297',
                filter: 'drop-shadow(0 0 8px rgba(202, 178, 151, 0.3))'
              }} 
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                style={{
                  padding: '0.6rem 1.2rem',
                  borderRadius: '1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  fontFamily: 'Fredoka, sans-serif',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  willChange: 'transform',
                  ...(activeSection === item.id ? {
                    background: 'linear-gradient(135deg, rgba(255, 232, 200, 0.25) 0%, rgba(237, 197, 143, 0.3) 100%)',
                    color: '#ffe8c8',
                    boxShadow: '0 0 15px rgba(255, 232, 200, 0.25)',
                    transform: 'translateY(-1px)',
                  } : {
                    background: 'rgba(255, 232, 200, 0.05)',
                    color: '#edc58f',
                  })
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== item.id) {
                    e.target.style.background = 'linear-gradient(135deg, rgba(255, 232, 200, 0.12) 0%, rgba(237, 197, 143, 0.18) 100%)';
                    e.target.style.transform = 'translateY(-2px) scale(1.03)';
                    e.target.style.boxShadow = '0 0 12px rgba(255, 232, 200, 0.2)';
                    e.target.style.color = '#ffe8c8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== item.id) {
                    e.target.style.background = 'rgba(255, 232, 200, 0.05)';
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = 'none';
                    e.target.style.color = '#edc58f';
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              padding: '0.5rem',
              borderRadius: '50%',
              background: 'rgba(20, 12, 8, 0.8)',
              backdropFilter: 'blur(10px)',
              color: '#ffe8c8',
              transition: 'all 0.3s ease',
            }}
            className="md:hidden"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navigation;
